import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  listStories,
  deleteStory,
  reembedStory,
  cancelEmbedRetries,
} from '../api/client';
import type { AdminStoryListEntry } from '../api/types';

type FilterKey = 'all' | 'embed_pending' | 'embed_failed' | 'in_refinement' | 'idle';

const FILTERS: { key: FilterKey; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'embed_pending', label: 'Embed pending' },
  { key: 'embed_failed', label: 'Embed failed' },
  { key: 'in_refinement', label: 'In refinement' },
  { key: 'idle', label: 'Idle' },
];

function getTypeBadgeClass(type: string) {
  switch (type) {
    case 'stranger':
      return 'bg-gray-100 text-gray-700';
    case 'crush':
      return 'bg-pink-100 text-pink-700';
    case 'ex':
      return 'bg-purple-100 text-purple-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
}

function formatYouIdentity(story: AdminStoryListEntry): string | null {
  const parts: string[] = [];
  if (story.youProfile?.name) parts.push(story.youProfile.name);
  else if (story.youProfile?.nickname) parts.push(story.youProfile.nickname);
  if (story.youProfile?.age) parts.push(story.youProfile.age);
  return parts.length ? parts.join(', ') : null;
}

export default function StoriesPage() {
  const [stories, setStories] = useState<AdminStoryListEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionId, setActionId] = useState<string | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterKey>('all');

  useEffect(() => {
    listStories(100, 0)
      .then((res) => setStories(res.stories))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    return stories.filter((s) => {
      switch (filter) {
        case 'embed_pending':
          return !s.embed_status.ready && !s.embed_status.embed_failure_reason;
        case 'embed_failed':
          return !!s.embed_status.embed_failure_reason;
        case 'in_refinement':
          return s.active_refinement_count > 0;
        case 'idle':
          return s.embed_status.ready && s.active_refinement_count === 0;
        case 'all':
        default:
          return true;
      }
    });
  }, [stories, filter]);

  const handleDelete = async (storyId: string) => {
    if (!confirm('Delete this story? This will also delete matches and vectors.')) return;
    setActionId(storyId);
    try {
      await deleteStory(storyId);
      setStories((prev) => prev.filter((s) => s.id !== storyId));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete story');
    } finally {
      setActionId(null);
      setOpenMenuId(null);
    }
  };

  const handleReembed = async (storyId: string) => {
    if (!confirm('Re-embed this story now? Pinecone vectors will be regenerated.')) return;
    setActionId(storyId);
    try {
      await reembedStory(storyId);
      // Optimistic flip — the next list refresh will pick up real DO state.
      setStories((prev) =>
        prev.map((s) =>
          s.id === storyId
            ? {
                ...s,
                embed_status: {
                  ...s.embed_status,
                  ready: true,
                  embed_failure_reason: null,
                },
              }
            : s,
        ),
      );
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to re-embed story');
    } finally {
      setActionId(null);
      setOpenMenuId(null);
    }
  };

  const handleCancelEmbed = async (storyId: string) => {
    if (!confirm('Cancel pending embed retries for this story?')) return;
    setActionId(storyId);
    try {
      await cancelEmbedRetries(storyId);
      setStories((prev) =>
        prev.map((s) =>
          s.id === storyId
            ? {
                ...s,
                embed_status: {
                  ...s.embed_status,
                  retry_count: null,
                  next_retry_at: null,
                  last_attempt_at: null,
                  embed_failure_reason: null,
                  last_error: null,
                },
              }
            : s,
        ),
      );
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to cancel embed retries');
    } finally {
      setActionId(null);
      setOpenMenuId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading stories...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        Error: {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Stories</h1>
        <span className="text-gray-500">
          {filtered.length} of {stories.length}
        </span>
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
              filter === f.key
                ? 'bg-pebbl-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="grid gap-4">
        {filtered.map((story) => {
          const youIdentity = formatYouIdentity(story);
          return (
            <div key={story.id} className="card hover:shadow-lg transition-shadow relative">
              <div className="flex justify-between items-start">
                <Link to={`/stories/${story.id}`} className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span
                      className={`text-xs font-medium px-2 py-0.5 rounded ${getTypeBadgeClass(
                        story.type,
                      )}`}
                    >
                      {story.type}
                    </span>
                    {youIdentity && (
                      <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">
                        You: {youIdentity}
                      </span>
                    )}
                    <EmbedBadge story={story} />
                    {story.active_refinement_count > 0 && (
                      <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded">
                        refinement ×{story.active_refinement_count}
                      </span>
                    )}
                    {story.revision_count > 0 && (
                      <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
                        rev {story.revision_count}
                      </span>
                    )}
                    <span className="text-xs text-gray-400">•</span>
                    {story.location.venue && (
                      <span className="text-xs text-gray-500">{story.location.venue}</span>
                    )}
                    <span className="text-xs text-gray-400">{story.location.city}</span>
                  </div>

                  <p className="text-gray-700 line-clamp-2">{story.rawText}</p>

                  <div className="mt-2 flex items-center gap-4 text-xs text-gray-500 flex-wrap">
                    {story.time.date && <span>Date: {story.time.date}</span>}
                    {story.time.dayPart && <span>{story.time.dayPart}</span>}
                    {story.time.range && ((r) => {
                      const s = new Date(r.startMs).toLocaleDateString();
                      const e = new Date(r.endMs - 1).toLocaleDateString();
                      return <span className="text-blue-600">Range: {s === e ? s : `${s} – ${e}`}</span>;
                    })(story.time.range)}
                    {story.sharedPlaceOfInteraction && (
                      <span>Shared: {story.sharedPlaceOfInteraction}</span>
                    )}
                    {story.frequency?.pattern && (
                      <span className="text-indigo-600">{story.frequency.pattern}</span>
                    )}
                    {story.event.trigger && <span>Trigger: {story.event.trigger}</span>}
                    {story.emotionalTags?.length > 0 && (
                      <span className="text-pink-600">
                        {story.emotionalTags.slice(0, 2).join(', ')}
                        {story.emotionalTags.length > 2 && '...'}
                      </span>
                    )}
                  </div>
                </Link>

                <div className="text-right text-xs text-gray-400 ml-4 shrink-0 flex flex-col items-end gap-2">
                  <div>
                    <div className="font-mono">{story.id.slice(0, 8)}...</div>
                    <div>{new Date(story.createdAt).toLocaleDateString()}</div>
                  </div>
                  {/* Kebab menu */}
                  <div className="relative">
                    <button
                      type="button"
                      className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 px-2 py-1 rounded text-xs"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setOpenMenuId(openMenuId === story.id ? null : story.id);
                      }}
                      disabled={actionId === story.id}
                    >
                      {actionId === story.id ? '…' : '⋯'}
                    </button>
                    {openMenuId === story.id && (
                      <div className="absolute right-0 mt-1 w-44 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                        <Link
                          to={`/stories/${story.id}`}
                          className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          View detail
                        </Link>
                        <button
                          type="button"
                          className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          onClick={() => handleReembed(story.id)}
                        >
                          Re-embed now
                        </button>
                        {(!story.embed_status.ready ||
                          story.embed_status.embed_failure_reason) && (
                          <button
                            type="button"
                            className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            onClick={() => handleCancelEmbed(story.id)}
                          >
                            Cancel embed retries
                          </button>
                        )}
                        <button
                          type="button"
                          className="block w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                          onClick={() => handleDelete(story.id)}
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {filtered.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No stories match this filter.
          </div>
        )}
      </div>
    </div>
  );
}

function EmbedBadge({ story }: { story: AdminStoryListEntry }) {
  const status = story.embed_status;
  if (status.ready) {
    return (
      <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">
        embed ready
      </span>
    );
  }
  if (status.embed_failure_reason) {
    return (
      <span
        className="text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded"
        title={status.embed_failure_reason}
      >
        embed failed
      </span>
    );
  }
  // List endpoint doesn't fetch DO state per row, so retry_count is null;
  // we just show a generic "pending" chip and let the detail page expose
  // the full retry timeline.
  return (
    <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded">
      embed pending
    </span>
  );
}
